/**
 * ThemeToggle — Botón reutilizable para alternar entre tema claro y oscuro
 *
 * QUÉ HACE:
 * - Muestra un botón con ícono de sol/luna según el tema activo
 * - Alterna entre light y dark al hacer clic
 * - Se adapta al estado colapsado/expandido del Sidebar
 *
 * QUÉ INVOCA: useThemeStore
 * QUÉ EXPORTA: ThemeToggle (named)
 *
 * QUÉ NO DEBE HACER:
 * - NO debe contener lógica de estilos de otros componentes
 * - NO debe gestionar persistencia (eso lo hace themeStore)
 */

import { Moon, Sun } from "lucide-react";
import toast from "react-hot-toast";
import { useThemeStore } from "@/store/themeStore";

interface ThemeToggleProps {
  /** Si el sidebar/contenedor está expandido (muestra label) */
  expanded?: boolean;
  /** Tipografía e ícono más chicos (pie del sidebar) */
  compact?: boolean;
}

export const ThemeToggle = ({
  expanded = true,
  compact = false,
}: ThemeToggleProps) => {
  const { theme, toggleTheme } = useThemeStore();
  const isDark = theme === "dark";

  const handleToggle = () => {
    toggleTheme();
    const next = isDark ? "claro" : "oscuro";
    toast(next === "oscuro" ? "Modo Oscuro activado" : "Modo Claro activado", {
      duration: 1800,
      id: "theme-toggle",
    });
  };

  const iconSize = compact ? 15 : 17;
  const padY = compact ? "py-1" : "py-1.5";
  const labelClass = compact
    ? "font-medium text-[10px] text-gray-500 dark:text-gray-400"
    : "font-semibold text-[11.5px]";

  return (
    <button
      onClick={handleToggle}
      title={isDark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
      className={`
        group flex w-full items-center gap-2 rounded-lg px-2.5 ${padY} transition-colors duration-150 ease-out
        text-gray-600 sidebar-item hover:bg-gray-100 dark:hover:bg-slate-700
        ${!expanded ? "justify-center" : ""}
      `}
    >
      {isDark ? (
        <Moon size={iconSize} className="flex-shrink-0 text-slate-400" />
      ) : (
        <Sun size={iconSize} className="flex-shrink-0 text-amber-400" />
      )}
      {expanded && (
        <span
          className={`${labelClass} whitespace-nowrap overflow-hidden text-ellipsis`}
        >
          {isDark ? "Modo Oscuro" : "Modo Claro"}
        </span>
      )}
    </button>
  );
};
